import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, errMsg, input, money } from './costingApi';

const ReviewRow = ({ s, onDone }) => {
  const [cost, setCost] = useState('');
  const save = async () => {
    try { await api.post(`/ledger/cogs-review/${s.id}`, { unit_cost: cost, update_product: true }); toast.success('COGS posted'); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-t border-slate-700" data-testid={`cogs-review-row-${s.id}`}>
      <td className="p-2">{s.date}</td><td className="p-2">{s.part_description}</td><td className="p-2 text-right">{s.quantity}</td>
      <td className="p-2 text-right">{money(s.total)}</td>
      <td className="p-2"><Input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Unit cost" className={`${input} h-8 w-28`} data-testid={`cogs-review-cost-${s.id}`} /></td>
      <td className="p-2"><Button size="sm" disabled={!(parseFloat(cost) > 0)} onClick={save} className="bg-emerald-600 h-8" data-testid={`cogs-review-save-${s.id}`}>Post COGS</Button></td>
    </tr>
  );
};

export const CogsReview = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/ledger/cogs-review').then(setRows).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2" data-testid="cogs-review">
      <p className="text-sm text-slate-300">Sales whose part had no cost when sold. COGS is held back (never silently R0) until you enter the unit cost.</p>
      <div className="overflow-x-auto border border-slate-700 rounded">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 text-slate-300 text-xs"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Part</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Sale</th><th className="p-2">Unit cost</th><th className="p-2"></th></tr></thead>
          <tbody>
            {rows.map((s) => <ReviewRow key={s.id} s={s} onDone={load} />)}
            {rows.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-400">Nothing to review.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const LedgerView = () => {
  const [tb, setTb] = useState(null);
  const [entries, setEntries] = useState([]);
  const [account, setAccount] = useState('');
  useEffect(() => { api.get('/ledger/trial-balance').then(setTb).catch(() => {}); }, []);
  useEffect(() => { api.get('/ledger/entries', { account: account || undefined, limit: 200 }).then(setEntries).catch(() => {}); }, [account]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-testid="ledger-view">
      <div className="border border-slate-700 rounded text-sm">
        <div className="p-2 bg-slate-800 font-semibold flex justify-between">Trial balance <span className={tb?.balanced ? 'text-emerald-400' : 'text-rose-400'} data-testid="trial-balance-status">{tb?.balanced ? 'Balanced' : 'Out of balance'}</span></div>
        {(tb?.accounts || []).map((a) => (
          <button key={a.code} onClick={() => setAccount(account === a.code ? '' : a.code)} className={`w-full flex justify-between px-2 py-1 border-t border-slate-700 text-left hover:bg-slate-800 ${account === a.code ? 'bg-slate-700' : ''}`} data-testid={`tb-account-${a.code}`}>
            <span>{a.code} {a.name}</span><span>{money(a.balance)}</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto border border-slate-700 rounded max-h-[520px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-800 text-slate-300 sticky top-0"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Ref</th><th className="p-2 text-left">Memo</th><th className="p-2 text-left">Lines</th></tr></thead>
          <tbody>{entries.map((e) => (
            <tr key={e.id} className="border-t border-slate-700 align-top">
              <td className="p-2 whitespace-nowrap">{e.date}</td><td className="p-2 font-mono text-[10px] max-w-[160px] truncate" title={e.ref}>{e.ref}</td><td className="p-2">{e.memo}</td>
              <td className="p-2 whitespace-nowrap">{e.lines.map((l) => <div key={l.account + l.debit + l.credit}>{l.debit ? 'Dr' : 'Cr'} {l.account_name} {money(l.debit || l.credit)}</div>)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};
